import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../contexts/firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import confetti from "canvas-confetti";
import WatchAdModal from "../components/ads/WatchAdModal";
import DashboardPageBanner from "../components/ads/DashboardPageBanner";
import Toast from "../components/Toast";
import ImageCard from "../components/ImageCard";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { deleteObject, ref as storageRef } from "firebase/storage";


export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser, credits, setCredits } = useAuth();
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [isAdModalOpen, setAdModalOpen] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 12;
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = images.slice(indexOfFirstImage, indexOfLastImage);

  const [selectedImage, setSelectedImage] = useState(null); // ⬅️ NEW

  const handleWatchAd = () => setAdModalOpen(true);

  const handlePreviousPage = () => setCurrentPage((prev) => prev - 1);
  const handleNextPage = () => setCurrentPage((prev) => prev + 1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleAdComplete = async () => {
    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      await updateDoc(userDocRef, { credits: credits + 1 });
      setCredits((c) => c + 1);
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setToast({ message: "🎉 1 Credit Earned!", type: "success" });
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to earn credit. Please try again.", type: "error" });
    }
  };

  const handleDownload = async (img) => {
    try {
      const resp = await fetch(img.imageUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `geneartai_image_${img.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to download image", type: "error" });
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "images", img.id));
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      setToast({ message: "Image deleted", type: "success" });
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to delete image", type: "error" });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u) setUser(u);
      else navigate("/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setFirstName(snap.data().firstName || "User");

        setLoadingImages(true);
        const q = query(
          collection(db, "users", user.uid, "images"),
          orderBy("timestamp", "desc")
        );
        const snapImgs = await getDocs(q);

        const now = Date.now();
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

        const validImages = [];

        for (const docSnap of snapImgs.docs) {
          const data = docSnap.data();
          const createdAt = data.timestamp?.toDate?.()?.getTime?.() ?? 0;

          if (now - createdAt > THIRTY_DAYS) {
            // Delete Firestore doc
            await deleteDoc(doc(db, "users", user.uid, "images", docSnap.id));

            // Delete from Storage (only if URL has path info)
            if (data.imageUrl) {
              try {
                const match = data.imageUrl.match(/\/o\/(.*?)\?alt/);
                if (match?.[1]) {
                  const path = decodeURIComponent(match[1]);
                  const imgRef = storageRef(storage, path);
                  await deleteObject(imgRef);
                }
              } catch (err) {
                console.warn("Storage delete failed for:", data.imageUrl, err);
              }
            }
          } else {
            validImages.push({ id: docSnap.id, ...data });
          }
        }

        setImages(validImages);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingImages(false);
      }
    })();
  }, [user]);



  useEffect(() => {
    const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isiOS) {
      const vp = document.querySelector("meta[name=viewport]");
      const orig = vp?.getAttribute("content");
      if (vp && orig) {
        vp.setAttribute("content", orig + ", maximum-scale=1");
        setTimeout(() => vp.setAttribute("content", orig), 500);
      }
    }
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const bgUrl = isMobile
    ? "https://geneartai.com/images/mobile1.png"
    : "https://geneartai.com/images/image1.png";

  return (
    <div className="relative min-h-screen font-sans text-white flex flex-col overflow-x-hidden">
      {/* Background */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: `url('${bgUrl}')`,
          backgroundAttachment: isMobile ? "scroll" : "fixed",
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
          duration={4000}
        />
      )}

      {/* Main */}
      <div className="relative z-10 flex flex-col flex-grow px-4 md:px-8 items-center">
        <div className="pt-16 sm:pt-16 md:pt-28"></div>

        <div className="hidden md:block">
          <DashboardPageBanner />
        </div>

        <div className="flex flex-col w-full max-w-8xl mt-6">
          <motion.div
            className="rounded-2xl p-8 bg-black/20 backdrop-blur-md shadow-2xl flex flex-col gap-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-white text-center">
              Welcome, {firstName} 👋
            </h1>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={handleWatchAd}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold py-2 px-5 rounded-full shadow-md hover:shadow-lg w-full md:w-auto"
              >
                Watch Ad to Earn Credit
              </button>
              <button
                onClick={() => navigate("/generate")}
                disabled={credits === 0}
                className={`py-2 px-5 rounded-full font-semibold w-full md:w-auto ${credits === 0
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-blue-800 hover:bg-[#166CB0] text-white shadow-md hover:shadow-lg"
                  }`}
              >
                {credits === 0 ? "No Credits Available" : "Generate AI Image"}
              </button>
            </div>

            <div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white text-center mb-6">
                🎨 Your Generated Images
              </h2>

              {loadingImages ? (
                <p className="text-center">Loading images...</p>
              ) : images.length === 0 ? (
                <p className="text-center">No images generated yet!</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {currentImages.map((img) => (
                    <ImageCard
                      key={img.id}
                      img={img}
                      onDownload={() => handleDownload(img)}
                      onDelete={() => handleDelete(img)}
                      onViewFullScreen={() => setSelectedImage(img)}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Pagination */}
        {images.length > imagesPerPage && (
          <div className="flex flex-col items-center justify-center mt-8 gap-4">
            <p className="text-gray-300 text-sm">
              Page {currentPage} of {Math.ceil(images.length / imagesPerPage)}
            </p>
            <div className="flex gap-4">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="px-4 py-1 text-sm rounded-full bg-[#166CB0] hover:bg-blue-800 text-white disabled:bg-gray-600 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                <ChevronLeftIcon className="h-3 w-3" />
                Prev
              </button>
              <button
                onClick={handleNextPage}
                disabled={indexOfLastImage >= images.length}
                className="px-4 py-1 text-sm rounded-full bg-[#166CB0] hover:bg-blue-800 text-white disabled:bg-gray-600 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                Next
                <ChevronRightIcon className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center text-xs text-gray-300 py-4 mt-auto">
          <div>© 2025 GeneArtAI — All Rights Reserved.</div>
          <div className="mt-2">
            <Link to="/privacy-policy" className="underline hover:text-white">
              Privacy Policy
            </Link>{" "}
            |{" "}
            <Link to="/terms-of-service" className="underline hover:text-white">
              Terms of Service
            </Link>
          </div>
        </footer>
      </div>

      {/* Fullscreen Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4">
          <div className="relative w-full h-full flex justify-center items-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-gray-800/70 hover:bg-gray-700 p-2 rounded-full text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage.imageUrl}
              alt="Full View"
              className="rounded-lg shadow-2xl max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Bottom Spacer */}
      <div className="block sm:hidden h-20" />

      <WatchAdModal
        isOpen={isAdModalOpen}
        onClose={() => setAdModalOpen(false)}
        onComplete={handleAdComplete}
        onError={(msg) => setToast({ message: msg, type: "error" })}
      />
    </div>
  );
}
